import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import BrandLogo from "@/components/BrandLogo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Menu, X, ArrowRight, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * Shared shell for public (non-app) pages.
 * Editorial · architectural · Swiss — not SaaS.
 */

// Primary links stay in the top nav — the four decision-making
// destinations most visitors need. Secondary audience/surface links
// live under a "More ▾" dropdown; legal/utility links are footer-only.
const PRIMARY_NAV = [
  { to: "/network", labelKey: "nav.network", label: "Network" },
  { to: "/programmable", labelKey: "nav.programmable", label: "Programmable" },
  { to: "/store", labelKey: "nav.store", label: "Store" },
  { to: "/partners", labelKey: "nav.partners", label: "Partners" },
];

const MORE_NAV = [
  { to: "/venues", labelKey: "nav.venues", label: "Venues" },
  { to: "/media-owners", labelKey: "nav.mediaOwners", label: "Media Owners" },
  { to: "/concierge", labelKey: "nav.concierge", label: "Concierge" },
  { to: "/demo", labelKey: "nav.demos", label: "Demos" },
  { to: "/franchise/apply", labelKey: "nav.franchise", label: "Franchise" },
];

export function PublicNav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);
  const { t } = useTranslation();
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  useEffect(() => {
    const onDoc = (e) => {
      if (moreOpen && moreRef.current && !moreRef.current.contains(e.target)) {
        setMoreOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [moreOpen]);
  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all ${
          scrolled
            ? "bg-black/70 backdrop-blur-xl border-b border-white/10"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="public-brand">
            <BrandLogo size={28} />
            <span className="font-display font-extrabold tracking-tight text-[15px]">
              Mirrorloops
            </span>
          </Link>
          <nav className="hidden lg:flex items-center gap-6 text-[13px] text-white/60 whitespace-nowrap">
            {PRIMARY_NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                className="hover:text-white transition"
                data-testid={`public-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {t(n.labelKey, n.label)}
              </Link>
            ))}
            <div className="relative" ref={moreRef}>
              <button
                type="button"
                onClick={() => setMoreOpen((v) => !v)}
                className="flex items-center gap-1 hover:text-white transition"
                data-testid="public-nav-more"
                aria-expanded={moreOpen}
                aria-haspopup="menu"
              >
                {t("nav.more", "More")}
                <ChevronDown size={13} className={`transition-transform ${moreOpen ? "rotate-180" : ""}`} />
              </button>
              {moreOpen && (
                <div
                  role="menu"
                  data-testid="public-nav-more-menu"
                  className="absolute right-0 mt-3 w-56 bg-[#141110] border border-white/10 backdrop-blur-xl shadow-2xl py-2"
                >
                  {MORE_NAV.map((n) => (
                    <Link
                      key={n.to}
                      to={n.to}
                      onClick={() => setMoreOpen(false)}
                      className="block px-4 py-2.5 text-[13px] text-white/70 hover:text-white hover:bg-white/[0.04] transition"
                      data-testid={`public-nav-more-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                    >
                      {t(n.labelKey, n.label)}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </nav>
          <div className="hidden md:flex items-center gap-3 whitespace-nowrap shrink-0">
            <LanguageSwitcher />
            <a
              href="https://work.mirrorloops.com"
              target="_blank"
              rel="noreferrer"
              data-testid="public-nav-operator-signin"
              className="hidden xl:inline-flex text-[10px] text-white/50 hover:text-[#E8D6A3] transition items-center gap-1 uppercase tracking-[0.14em] font-mono"
              title="Operator sign-in — invite-only"
            >
              {t("nav.operatorSignin", "Operator sign-in")}
            </a>
            <Link to="/login" className="text-sm text-white/70 hover:text-white">
              {t("nav.signin", "Sign in")}
            </Link>
            <Link
              to="/register"
              data-testid="public-nav-launch"
              className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 bg-white text-black hover:bg-white/90 transition"
            >
              {t("nav.launch", "Launch a campaign")} <ArrowRight size={14} />
            </Link>
          </div>
          <button
            className="md:hidden text-white p-2"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
          >
            <Menu size={22} />
          </button>
        </div>
      </header>
      {open && (
        <div className="fixed inset-0 z-[60] bg-[#0A0908] md:hidden overflow-y-auto">
          <div className="flex items-center justify-between px-6 h-16 border-b border-white/10 sticky top-0 bg-[#0A0908]">
            <div className="flex items-center gap-3">
              <BrandLogo size={30} />
              <span className="font-display font-extrabold">Mirrorloops</span>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Close" className="p-2">
              <X size={22} />
            </button>
          </div>
          <div className="p-6 flex flex-col">
            {PRIMARY_NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className="py-4 text-2xl font-display font-extrabold border-b border-white/5"
              >
                {t(n.labelKey, n.label)}
              </Link>
            ))}
            <div className="mt-4 mb-2 text-[10px] uppercase tracking-[0.22em] text-white/40 font-mono">
              {t("nav.more", "More")}
            </div>
            {MORE_NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className="py-3 text-base text-white/70 border-b border-white/5"
              >
                {t(n.labelKey, n.label)}
              </Link>
            ))}
            <div className="mt-6">
              <LanguageSwitcher mobile />
            </div>
            <Link
              to="/login"
              className="mt-6 py-3 text-center border border-white/15 text-white"
              onClick={() => setOpen(false)}
            >
              {t("nav.signin", "Sign in")}
            </Link>
            <a
              href="https://work.mirrorloops.com"
              target="_blank"
              rel="noreferrer"
              className="mt-2 py-3 text-center border border-white/10 text-white/60 text-xs uppercase tracking-[0.18em] font-mono"
              onClick={() => setOpen(false)}
              data-testid="public-nav-operator-signin-mobile"
            >
              {t("nav.operatorSignin", "Operator sign-in")}
            </a>
            <Link
              to="/register"
              className="mt-2 py-3 text-center bg-white text-black font-bold"
              onClick={() => setOpen(false)}
            >
              {t("nav.launch", "Launch a campaign")}
            </Link>
          </div>
        </div>
      )}
    </>
  );
}

/** Recurring visual grammar strip: SCREEN · PLACE · MOMENT · CONTEXT · PROGRAM · LOOP */
export function GrammarStrip() {
  const words = ["SCREEN", "PLACE", "MOMENT", "CONTEXT", "PROGRAM", "LOOP"];
  return (
    <div className="border-y border-white/10 bg-black overflow-hidden" data-testid="grammar-strip">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-8 grid grid-cols-3 md:grid-cols-6 gap-4">
        {words.map((w, i) => (
          <div
            key={w}
            className="flex items-center gap-3 font-mono text-[10px] text-white/40 uppercase tracking-[0.28em]"
          >
            <span className="text-[#E8D6A3]">{String(i + 1).padStart(2, "0")}</span>
            <span>{w}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function PublicFooter() {
  return (
    <footer className="bg-[#0A0908] text-white/60 border-t border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 grid md:grid-cols-6 gap-10">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3 mb-6">
            <BrandLogo size={36} />
            <span className="font-display font-extrabold text-white text-lg">Mirrorloops</span>
          </div>
          <p className="max-w-sm text-sm leading-relaxed">
            Advertising for the physical world. A programmable media network
            connecting screens, places, moments, and context — with proof of
            play at every endpoint.
          </p>
        </div>
        <FooterCol
          title="Product"
          links={[
            ["Network", "/network"],
            ["Programmable", "/programmable"],
            ["Store", "/store"],
            ["Calculator", "/calculator"],
            ["Demos", "/demo"],
          ]}
        />
        <FooterCol
          title="For"
          links={[
            ["Advertisers", "/register"],
            ["Venues", "/venues"],
            ["Media Owners", "/media-owners"],
            ["Partners", "/partners"],
            ["Concierge", "/concierge"],
            ["Franchise", "/franchise/apply"],
          ]}
        />
        <FooterCol
          title="Labs"
          links={[
            ["Node", "/node"],
            ["OS", "/os"],
            ["MPMP", "/labs/mpmp"],
            ["Integration docs", "https://github.com/mirrorloops/mirrorloops/blob/main/INTEGRATION.md"],
            ["Sitemap", "/sitemap_index.xml"],
          ]}
        />
        <FooterCol
          title="Company"
          links={[
            ["Sign in", "/login"],
            ["Register", "/register"],
            ["Operator sign-in", "https://work.mirrorloops.com"],
            ["Contact", "mailto:hello@mirrorloops.com"],
          ]}
        />
      </div>
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-8 flex flex-col md:flex-row items-center justify-between gap-3 text-xs border-t border-white/10">
        <div className="text-white/50" data-testid="public-copyright">
          © Mirrorloops Opc Pvt. Ltd.
        </div>
        <div className="flex items-center gap-6 text-white/40">
          <a href="#" className="hover:text-white">Privacy</a>
          <a href="#" className="hover:text-white">Terms</a>
          <a href="#" className="hover:text-white">Security</a>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-4">{title}</div>
      <ul className="space-y-3">
        {links.map(([l, href]) => (
          <li key={l}>
            {href.startsWith("/") ? (
              <Link to={href} className="text-sm hover:text-white transition">{l}</Link>
            ) : (
              <a
                href={href}
                target={href.startsWith("http") ? "_blank" : undefined}
                rel={href.startsWith("http") ? "noreferrer" : undefined}
                className="text-sm hover:text-white transition"
                data-testid={href === "https://work.mirrorloops.com" ? "public-footer-operator-signin" : undefined}
              >
                {l}
              </a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

import CompareTray from "@/components/store/CompareTray";

export default function PublicShell({ children }) {
  return (
    <div className="bg-[#0A0908] text-white overflow-x-hidden font-sans min-h-screen flex flex-col">
      <PublicNav />
      <main className="flex-1">{children}</main>
      <CompareTray />
      <PublicFooter />
    </div>
  );
}
